import { useState } from 'react';
import { Minus, Plus, Trash2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatCurrency } from '@/lib/format';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';

export interface TabLineItemRowData {
  id: string;
  productName: string;
  quantity: number;
  lineTotal: number;
}

interface TabLineItemRowProps {
  item: TabLineItemRowData;
  disabled: boolean;
  loading: boolean;
  error: string | null;
  onQuantityChange: (quantity: number) => void;
  /** When true, minus is disabled (e.g. last line on the tab must use Remove order). */
  disableDecrease?: boolean;
}

const STEP_BUTTON_CLASS =
  'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-900 transition active:scale-95 disabled:opacity-50 dark:border-white/10 dark:bg-gray-800 dark:text-white';

/**
 * Touch-friendly quantity controls for a single tab line item.
 */
export function TabLineItemRow({
  item,
  disabled,
  loading,
  error,
  onQuantityChange,
  disableDecrease = false,
}: TabLineItemRowProps) {
  const [confirmRemove, setConfirmRemove] = useState(false);

  const handleDecrease = () => {
    if (disableDecrease) return;
    if (item.quantity <= 1) {
      setConfirmRemove(true);
      return;
    }
    onQuantityChange(item.quantity - 1);
  };

  return (
    <>
      <li
        className={cn(
          'flex min-h-12 flex-col gap-2 rounded-xl border border-gray-200 px-3 py-3 dark:border-white/10',
          (disabled || loading) && 'opacity-70',
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-gray-900 dark:text-white">{item.productName}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {formatCurrency(item.lineTotal)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={`Decrease ${item.productName}`}
              className={STEP_BUTTON_CLASS}
              disabled={disabled || loading || disableDecrease}
              onClick={handleDecrease}
            >
              <Minus className="h-5 w-5" aria-hidden />
            </button>
            <span
              className="min-w-[2ch] text-center text-lg font-semibold tabular-nums text-gray-900 dark:text-white"
              aria-live="polite"
            >
              {item.quantity}
            </span>
            <button
              type="button"
              aria-label={`Increase ${item.productName}`}
              className={STEP_BUTTON_CLASS}
              disabled={disabled || loading}
              onClick={() => onQuantityChange(item.quantity + 1)}
            >
              <Plus className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>
        {error && (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        )}
      </li>

      <Modal
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        title="Are you sure you want to remove this item?"
        description={`Remove all ${item.quantity}× ${item.productName} from this tab?`}
        size="sm"
        footer={
          <>
            <Button variant="outline" size="sm" onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              loading={loading}
              leftIcon={<Trash2 className="h-4 w-4" aria-hidden />}
              onClick={() => {
                setConfirmRemove(false);
                onQuantityChange(0);
              }}
            >
              Remove
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600 dark:text-gray-300">
          This cannot be undone. The tab must keep at least one product.
        </p>
      </Modal>
    </>
  );
}
